import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { safeNext } from "@/auth/loginUrl";
import { errorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, UserCheck } from "lucide-react";
import { createPageUrl } from "@/utils";

export default function CompleteProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    phone: user?.phone || "",
    address: user?.address || "",
    city: user?.city || "",
    state: user?.state || "",
    zip: user?.zip || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const updated = await api.me.update({ ...form, display_name: form.full_name });
      setUser(updated);
      navigate(safeNext(params.get("next"), createPageUrl("Events")), { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Could not save your profile."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 w-full max-w-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-indigo-100 p-2 rounded-xl">
            <UserCheck className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Complete Your Profile</h1>
            <p className="text-sm text-gray-500">We need a few details before you continue</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
          <div>
            <Label>Full Name *</Label>
            <Input
              value={form.full_name}
              onChange={e => set("full_name", e.target.value)}
              placeholder="Enter your full name"
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label>Email *</Label>
            <Input value={user?.email || ""} disabled className="mt-1 bg-gray-50" />
          </div>

          <div>
            <Label>Phone Number *</Label>
            <Input
              type="tel"
              value={form.phone}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                let formatted = digits;
                if (digits.length >= 7) formatted = `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
                else if (digits.length >= 4) formatted = `${digits.slice(0,3)}-${digits.slice(3)}`;
                set("phone", formatted);
              }}
              placeholder="000-000-0000"
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label>Street Address *</Label>
            <Input
              value={form.address}
              onChange={e => set("address", e.target.value)}
              placeholder="123 Main St"
              required
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>City *</Label>
              <Input
                value={form.city}
                onChange={e => set("city", e.target.value)}
                placeholder="New York"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>State *</Label>
              <Input
                value={form.state}
                onChange={e => set("state", e.target.value)}
                placeholder="NY"
                required
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label>ZIP Code *</Label>
            <Input
              value={form.zip}
              onChange={e => set("zip", e.target.value)}
              placeholder="10001"
              required
              className="mt-1"
            />
          </div>

          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-indigo-600 hover:bg-indigo-700 mt-2"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : "Save & Continue"}
          </Button>
        </form>
      </div>
    </div>
  );
}